import { CloudUpload } from "@mui/icons-material";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useUpsertDocument } from "core/documents/hooks";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { erroredAxiosResponse } from "utils";

type Props = {
  tempWorkerId: number;
  kind: string;
  label: string;
}

const CreateDocumentForm = ({ tempWorkerId, kind, label }: Props) => {
  const uploadFile = useUpsertDocument(tempWorkerId, {
    onSuccess: () => {
        toast.success('Le fichier a été uploadé');
    },
    onError: (responseError: any) => toast.error(erroredAxiosResponse(responseError, 'Le document n\'a pas pu être uploadé')),
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    uploadFile.mutate({
      name: label,
      tempWorkerId,
      key: kind,
      file: acceptedFiles[0]
    });
  }, [tempWorkerId, kind, label, uploadFile]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/heic': ['.heic'],
      'image/heif': ['.heif']
    },
    multiple: false
  });

  return (
    <Stack
      {...getRootProps()}
      sx={{
        border: isDragActive ? '1px dashed #1976d2' : '1px solid #eee',
        borderRadius: '0.5rem',
        height: '192px',
        backgroundColor: isDragActive ? '#f8f9fa' : 'white',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      {uploadFile.isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <input {...getInputProps()} />
          <Box sx={{
            border: '1px solid #eee',
            borderRadius: '0.5rem',
            padding: '1.75rem',
            position: 'relative',
          }}>
            <CloudUpload sx={{ 
              fontSize: '2rem', 
              color: isDragActive ? '#1976d2' : '#aaa', 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              transition: 'color 0.2s ease-in-out'
            }} />
          </Box>
          <Typography variant="body1" sx={{ mt: 2, color: 'secondary.main', fontWeight: '500' }}>
            {isDragActive ? 'Déposez le fichier ici' : 'Importer un document'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            PDF, JPG, PNG, ou HEIC
          </Typography>
        </>
      )}
    </Stack>
  );
};

export default CreateDocumentForm;